import styled from '@emotion/styled'
import React from 'react'
import { BusinessSubscription } from './BusinessCalendarQueries'
import { useBillingPortal } from './useBillingPortal'
import { Hugeicon } from '../../../icons/Hugeicon'

type Props = {
  organizationId: string
  subscription: BusinessSubscription
}

const BillingSection: React.FC<Props> = ({ organizationId, subscription }) => {
  const { openPortal, loading } = useBillingPortal(organizationId)

  const handleManageBilling = () => {
    void openPortal()
  }

  const isYearly = subscription.billingInterval === 'yearly'
  const calendarPrice = isYearly ? '$250' : '$25'
  const interval = isYearly ? 'year' : 'month'

  return (
    <Section>
      <SectionTitle>Your Plan</SectionTitle>

      {subscription.trialActive && (
        <TrialInfoBox>
          <TrialInfoIcon />
          <TrialInfoContent>
            <TrialInfoTitle>Free trial — {subscription.trialDaysRemaining} days remaining</TrialInfoTitle>
            <TrialInfoText>
              Your calendar will continue working after the trial ends. You&apos;ll be automatically charged using
              your payment method on file. You can cancel anytime before the trial ends.
            </TrialInfoText>
          </TrialInfoContent>
        </TrialInfoBox>
      )}

      <PlanCards>
        <PlanCard>
          <CurrentBadge>Current Plan</CurrentBadge>
          <PlanName>Calendar</PlanName>
          <PlanPrice>
            {calendarPrice}<PlanInterval>/{interval}</PlanInterval>
          </PlanPrice>
          <PlanFeatureList>
            <PlanFeatureItem>Embeddable event calendar</PlanFeatureItem>
            <PlanFeatureItem>Event management dashboard</PlanFeatureItem>
            <PlanFeatureItem>Network distribution</PlanFeatureItem>
            <PlanFeatureItem>Analytics & reporting</PlanFeatureItem>
          </PlanFeatureList>
        </PlanCard>
      </PlanCards>

      <BillingFooter>
        <ManageButton onClick={handleManageBilling} disabled={loading}>
          {loading ? 'Loading...' : 'Manage Billing'}
        </ManageButton>
        <HelpText>Update payment method, view invoices, or cancel your subscription through Stripe.</HelpText>
      </BillingFooter>
    </Section>
  )
}

export default BillingSection

const Section = styled.div``

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 24px;
`

const PlanCards = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-bottom: 32px;
  max-width: 400px;
`

const PlanCard = styled.div`
  position: relative;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  border: 2px solid #1e3a5f;
  background: #f9fafb;
`

const CurrentBadge = styled.div`
  position: absolute;
  top: -11px;
  left: 20px;
  background: #1e3a5f;
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const PlanName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #102638;
  margin-top: 4px;
  margin-bottom: 8px;
`

const PlanPrice = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: #102638;
  margin-bottom: 20px;
`

const PlanInterval = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
`

const PlanFeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const PlanFeatureItem = styled.li`
  font-size: 13px;
  color: #374151;
  padding: 5px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '\\2713';
    flex-shrink: 0;
  }
`

const BillingFooter = styled.div`
  padding-top: 8px;
`

const ManageButton = styled.button`
  background: #1e3a5f;
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: #2d4a6f;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const HelpText = styled.p`
  font-size: 12px;
  color: #9ca3af;
  margin: 8px 0 0;
`

const TrialInfoBox = styled.div`
  display: flex;
  gap: 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`

const TrialInfoIcon = () => (
  <Hugeicon name="alert-circle" size={20} style={{ flexShrink: 0, color: '#3b82f6', marginTop: 2 }} />
)

const TrialInfoContent = styled.div`
  flex: 1;
`

const TrialInfoTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 4px;
`

const TrialInfoText = styled.div`
  font-size: 13px;
  color: #1e40af;
  line-height: 1.5;
`
